import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { AuthService } from '../../auth/auth.service';
import { takeUntilDestroy } from '../../common/take-until-destroy.decorator';
import { DefaultSocketService } from '../../common/websocket';
import { LoggerService } from '../../logger';

import { EndCallParams } from './model/end-call-params.model';
import { HubMethods } from './model/hub-methods.model';
import { StartCallParams } from './model/start-call-params.model';
import { WriteCallAudioDataParams } from './model/write-call-audio-data-params.model';

@Injectable({ providedIn: 'root' })
export class SocketService extends DefaultSocketService implements OnDestroy {
    readonly message: Observable<{ hubMethod: string; payload: any }>;
    private readonly _messageSubj: Subject<{ hubMethod: string; payload: any }>;

    constructor(
        private readonly _authService2: AuthService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _logger: LoggerService,
    ) {
        super(_authService2);

        this._messageSubj = new Subject();
        this.message = this._messageSubj.asObservable();

        this._setupBindings();
    }

    ngOnDestroy(): void {
        this._destroy();
    }

    async retrieveCurrentCall(): Promise<void> {
        await this.send(HubMethods.RetrieveCurrentCall);
    }

    async startCall(params: StartCallParams): Promise<void> {
        await this.send(HubMethods.StartCall, params);
    }

    async writeCallAudioData(params: WriteCallAudioDataParams): Promise<void> {
        await this.send(HubMethods.WriteCallAudioData, params);
    }

    async endCall(params: EndCallParams): Promise<void> {
        await this.send(HubMethods.EndCall, params);
    }

    protected _websocketUrl(): string {
        return `${this._appConfig.API_ENDPOINTS.LOCAL_CALL ?? ''}/ws`;
    }

    private _setupBindings(): void {
        if (this.created) {
            this._bind();
        }
        this.didCreate
            .pipe(
                tap((_socketService) => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        if (this.connected) {
            void this.retrieveCurrentCall();
        }
        this.didConnect
            .pipe(
                tap((_socketService) => {
                    void this.retrieveCurrentCall();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        const methods: HubMethods[] = [
            HubMethods.OnRetrieveCurrentCall,
            HubMethods.OnCallState,
        ];

        methods.forEach((method) => {
            this.on(method, (payload) => {
                this._logger.debug({ method, payload });
                this._messageSubj.next({
                    hubMethod: method,
                    payload,
                });
            });
        });
    }
}
